import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera } from "lucide-react";
import { EXTRAS, FIXED_EXTRAS, PRICE_LEVELS } from "@/data/services";

const PROPERTY_TYPES = [
  "Vivienda",
  "Local comercial",
  "Comunidad de vecinos",
  "Oficina",
  "Otro",
];

const TIMINGS = [
  "Lo antes posible (urgente)",
  "En las próximas 2 semanas",
  "En 1 mes",
  "En 1-3 meses",
  "Estoy planificando, sin urgencia",
];

export default function Step2(props) {
  const {
    item,
    quantity, setQuantity,
    priceLevelId, setPriceLevelId,
    selectedExtras, setSelectedExtras,
    selectedFixed, setSelectedFixed,
    notes, setNotes,
    clientName, setClientName,
    clientPhone, setClientPhone,
    clientZone, setClientZone,
    propertyType, setPropertyType,
    timing, setTiming,
  } = props;

  const toggle = (arr, setter, id) => {
    setter(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="calc-step-2">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#004B87] font-semibold mb-2">Paso 2</div>
        <h2 className="font-display font-semibold text-2xl md:text-3xl text-slate-900 tracking-tight">
          Detalles, medición y extras
        </h2>
        <p className="text-slate-500 mt-2 text-sm">
          Estás presupuestando: <strong className="text-slate-900">{item?.name}</strong> · base desde {item?.price} €/{item?.unit}
        </p>
      </div>

      {/* Medición & Nivel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 rounded-xl border border-slate-200 bg-white p-5">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Medición ({item?.unit})</Label>
          <Input
            data-testid="quantity-input"
            type="number"
            min="0"
            step="0.5"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="mt-3 text-2xl font-display font-semibold h-14 focus-visible:ring-[#004B87] focus-visible:ring-offset-0"
            placeholder={item?.unit === 'm²' ? 'Introduce los m² aproximados' : item?.unit === 'ml' ? 'Introduce los ml aproximados' : '1'}
          />
          <div className="text-xs text-slate-500 mt-2">Introduce los {item?.unit} aproximados.</div>
        </div>

        <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Nivel de precio</Label>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {PRICE_LEVELS.map(p => {
              const active = priceLevelId === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setPriceLevelId(p.id)}
                  data-testid={`price-level-${p.id}`}
                  className={[
                    "p-3.5 rounded-lg border text-left transition-all h-full",
                    active ? "border-[#004B87] bg-[#E6F0FA]/50 ring-2 ring-[#004B87]/10" : "border-slate-200 bg-white hover:border-[#004B87]/50"
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-display font-semibold text-slate-900 leading-tight">{p.label}</div>
                    <div className={"text-[11px] font-semibold shrink-0 " + (active ? "text-[#004B87]" : "text-slate-400")}>×{p.factor.toFixed(2)}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2 leading-snug">{p.hint}</div>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
            Tarifas orientativas calculadas para trabajos profesionales en Valencia y provincia, realizados por empresa legal, con equipo propio, personal asegurado, garantía, herramientas, desplazamientos y acabados de calidad.
          </p>
        </div>
      </div>

      {/* Suplementos % */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex items-baseline justify-between">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Suplementos sobre obra (%)</Label>
          <span className="text-[11px] text-slate-400">Marca los que apliquen</span>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          {EXTRAS.map(ex => {
            const active = selectedExtras.includes(ex.id);
            return (
              <label
                key={ex.id}
                htmlFor={`ex-${ex.id}`}
                className={[
                  "flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-all",
                  active ? "border-[#004B87] bg-[#E6F0FA]/50" : "border-slate-200 hover:border-[#004B87]/50"
                ].join(" ")}
              >
                <Checkbox
                  id={`ex-${ex.id}`}
                  checked={active}
                  onCheckedChange={() => toggle(selectedExtras, setSelectedExtras, ex.id)}
                  data-testid={`extra-pct-${ex.id}`}
                  className="mt-0.5 data-[state=checked]:bg-[#004B87] data-[state=checked]:border-[#004B87]"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900">{ex.label}</div>
                    <div className="text-xs font-semibold text-[#004B87]">+{Math.round(ex.pct * 100)}%</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{ex.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Conceptos fijos */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex items-baseline justify-between">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Conceptos adicionales (€)</Label>
          <span className="text-[11px] text-slate-400">Opcional</span>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
          {FIXED_EXTRAS.map(f => {
            const active = selectedFixed.includes(f.id);
            return (
              <label
                key={f.id}
                htmlFor={`fx-${f.id}`}
                className={[
                  "flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-all",
                  active ? "border-[#004B87] bg-[#E6F0FA]/50" : "border-slate-200 hover:border-[#004B87]/50"
                ].join(" ")}
              >
                <Checkbox
                  id={`fx-${f.id}`}
                  checked={active}
                  onCheckedChange={() => toggle(selectedFixed, setSelectedFixed, f.id)}
                  data-testid={`extra-fixed-${f.id}`}
                  className="mt-0.5 data-[state=checked]:bg-[#004B87] data-[state=checked]:border-[#004B87]"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900">{f.label}</div>
                    <div className="text-xs font-semibold text-[#004B87]">+{f.amount}€</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{f.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Datos de contacto */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex items-baseline justify-between">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Tus datos para la visita técnica</Label>
          <span className="text-[11px] text-slate-400">Recomendado · agiliza la respuesta</span>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="client-name" className="text-xs text-slate-600">Nombre</Label>
            <Input
              id="client-name"
              data-testid="client-name-input"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Ej: Juan García"
              className="mt-1.5 focus-visible:ring-[#004B87] focus-visible:ring-offset-0"
            />
          </div>
          <div>
            <Label htmlFor="client-phone" className="text-xs text-slate-600">Teléfono</Label>
            <Input
              id="client-phone"
              data-testid="client-phone-input"
              type="tel"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              placeholder="Ej: 612 345 678"
              className="mt-1.5 focus-visible:ring-[#004B87] focus-visible:ring-offset-0"
            />
          </div>
          <div>
            <Label htmlFor="client-zone" className="text-xs text-slate-600">Dirección o zona</Label>
            <Input
              id="client-zone"
              data-testid="client-zone-input"
              value={clientZone}
              onChange={(e) => setClientZone(e.target.value)}
              placeholder="Ej: Cabanyal, Valencia"
              className="mt-1.5 focus-visible:ring-[#004B87] focus-visible:ring-offset-0"
            />
          </div>
          <div>
            <Label htmlFor="property-type" className="text-xs text-slate-600">Tipo de vivienda / local</Label>
            <select
              id="property-type"
              data-testid="property-type-select"
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:border-[#004B87] focus:ring-2 focus:ring-[#004B87]/15 focus:outline-none"
            >
              <option value="">Selecciona…</option>
              {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="timing" className="text-xs text-slate-600">¿Cuándo quieres realizar el trabajo?</Label>
            <select
              id="timing"
              data-testid="timing-select"
              value={timing}
              onChange={(e) => setTiming(e.target.value)}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:border-[#004B87] focus:ring-2 focus:ring-[#004B87]/15 focus:outline-none"
            >
              <option value="">Selecciona…</option>
              {TIMINGS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-5 flex items-start gap-3 rounded-lg bg-[#E6F0FA]/50 border border-[#004B87]/15 p-3.5">
          <Camera className="w-4 h-4 text-[#004B87] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-700 leading-relaxed">
            <strong>¿Tienes fotos del trabajo?</strong> Puedes enviárnoslas directamente por WhatsApp tras solicitar la visita técnica, para valorar mejor la estimación.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Comentarios adicionales (opcional)</Label>
        <textarea
          data-testid="notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Ej: planta 4 sin ascensor, hay que retirar muebles, materiales específicos…"
          className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm focus:border-[#004B87] focus:ring-2 focus:ring-[#004B87]/15 focus:outline-none"
        />
      </div>
    </div>
  );
}
